import { Injectable } from '@angular/core';
import { IndividualConfig, ToastrService } from 'ngx-toastr';
import { Observable, ReplaySubject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { SharedCapitalsMarketService } from '@core/services/shared';
import { Notification } from 'app/layout/components-common/notifications/notifications.types';
import { DecimalPipe } from '@angular/common';

@Injectable({ providedIn: 'root' })
export class NotificationsService {


  // -----------------------------------------------------------------------------------------------------
  // @ Public declaration vars
  // -----------------------------------------------------------------------------------------------------
  // -----------------------------------------------------------------------------------------------------
  // @ Private declaration vars
  // -----------------------------------------------------------------------------------------------------
  private audioNotify: HTMLAudioElement;
  private _notifications: ReplaySubject<Notification[]> = new ReplaySubject<Notification[]>(1);


  /**
   * Constructor
   */
  constructor(
    private _httpClient: HttpClient,
    private _toastr: ToastrService,
    private _sharedCapitalsMkS: SharedCapitalsMarketService,
    private _numbPipe: DecimalPipe
  ) {
    const cached = this.getNotificationsFromSession();
    this._notifications.next(cached); // Inicializa ReplaySubject con cache
  }

  private getNotificationsFromSession(): Notification[] {
    const stored = sessionStorage.getItem('lstAlarms');
    return stored ? JSON.parse(stored) : [];
  }

  destroy(): void {
    this._notifications.next([]);
    this._notifications.complete()
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for notifications
   */
  get notifications$(): Observable<Notification[]> {
    return this._notifications.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------


  private playSound() {
    this.audioNotify = new Audio();
    this.audioNotify.src = "assets/audio/alarm_notify.mp3";
    this.audioNotify.load();
    this.audioNotify.play();
  }


  /**
   * Método para obtener el ícono según el tipo de notificación
   */
  private getIconByType(type: string): string {
    const icons = {
      success: 'heroicons_solid:check-circle',
      error: 'heroicons_solid:x-circle',
      warning: 'heroicons_solid:exclamation-circle',
      info: 'heroicons_solid:information-circle',
    };
    return icons[type] || 'heroicons_solid:bell'; // Default icon
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------



  /**
   * Create a notification
   *
   * @param notification
   */
  create(notification: Notification): Observable<Notification> {
    return this.notifications$.pipe(
      take(1),
      switchMap((notifications) =>
        this._httpClient
          .post<Notification>('api/common/notifications', { notification })
          .pipe(
            map((newNotification) => {
              // Update the notifications with the new notification
              this._notifications.next([...notifications, newNotification]);

              // Return the new notification from observable
              return newNotification;
            })
          )
      )
    );
  }

  /**
   * Update the notification
   *
   * @param id
   * @param notification
   */
  update(id: string, notification: Notification): Observable<Notification> {
    return this.notifications$.pipe(
      take(1),
      switchMap((notifications) =>
        this._httpClient
          .patch<Notification>('api/common/notifications', { id, notification, })
          .pipe(
            map((updatedNotification: Notification) => {
              // Find the index of the updated notification
              const index = notifications.findIndex((item) => item.id === id);

              // Update the notification
              notifications[index] = updatedNotification;

              // Update the notifications
              this._notifications.next(notifications);

              // Return the updated notification
              return updatedNotification;
            })
          )
      )
    );
  }

  /**
   * Delete the notification
   *
   * @param id
   */
  delete(id: string): Observable<boolean> {
    return this.notifications$.pipe(
      take(1),
      switchMap((notifications) =>
        this._httpClient
          .delete<boolean>('api/common/notifications', { params: { id } })
          .pipe(
            map((isDeleted: boolean) => {
              // Find the index of the deleted notification
              const index = notifications.findIndex((item) => item.id === id);

              // Delete the notification
              notifications.splice(index, 1);

              // Update the notifications
              this._notifications.next(notifications);

              // Return the deleted status
              return isDeleted;
            })
          )
      )
    );
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead(): Observable<boolean> {
    return this.notifications$.pipe(
      take(1),
      switchMap((notifications) =>
        this._httpClient
          .get<boolean>('api/common/notifications/mark-all-as-read')
          .pipe(
            map((isUpdated: boolean) => {
              // Go through all notifications and set them as read
              notifications.forEach((notification, index) => {
                notifications[index].read = true;
              });

              // Update the notifications
              this._notifications.next(notifications);

              // Return the updated status
              return isUpdated;
            })
          )
      )
    );
  }

  /**
   * Agregar una nueva notificación desde un Toast
   */
  addNotificationFromToast(type: string, title: string, message: string): void {
    const newNotification: Notification = {
      id: Date.now().toString(),
      icon: this.getIconByType(type), // Obtiene el ícono según el tipo de notificación
      title,
      description: message,
      time: new Date().toISOString(),
      read: false,
    };

    this.notifications$.pipe(take(1)).subscribe((notifications) => {
      this._notifications.next([...notifications, newNotification]);
    });
  }



  public addNotificationFromWebSocket(alert: any): void {
    
    let labelOverrider = (alert.limiteAlcanzado.toLowerCase().includes('ximo')) ? 'superior' : 'inferior',
    getConfigAlrm = JSON.parse( localStorage.getItem('stopSoundAlarm'))

    const newNotification: Notification = {
      id: Date.now().toString(),
      icon: 'heroicons_solid:bell',
      title: `Precio ${labelOverrider} - ${alert.emisora} ${alert.serie} - $${this._numbPipe.transform( alert.precio, '1.2-2')}` ,
      description: `Último precio: $${this._numbPipe.transform( alert.precioAlarma, '1.2-2')}`,
      time: `${alert.fecha} - ${alert.hora}`,
      // time: new Date().toISOString(),
      read: false,
    };

    this.notifications$.pipe(take(1)).subscribe((notifications) => {
      const updated = [newNotification, ...notifications];
     if (!getConfigAlrm || getConfigAlrm !== true ){ this.playSound();}
      // Actualiza ReplaySubject
      this._notifications.next(updated);

      // Guarda en sessionStorage
      sessionStorage.setItem('lstAlarms', JSON.stringify(updated));

      const overrideConfig: Partial<IndividualConfig> = { timeOut: 6000 }; // time millis seconds, show toastr


      // ✅ Mostrar toast
      const toast = this._toastr.success(
        `de $${this._numbPipe.transform( alert.precio, '1.2-2')} alcanzó los $${this._numbPipe.transform( alert.precioAlarma, '1.2-2')}`,
        `Precio ${labelOverrider} - ${alert.emisora} ${alert.serie}`,
        overrideConfig
      );

      toast.onHidden.subscribe(() => {
        this._sharedCapitalsMkS.setDataCapitals({ reload: true }, 'alarmNotify')
      });

    });
  }
}
